import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  Dimensions,
  PixelRatio,
  Platform,
  ActivityIndicator,
  StatusBar,
  Modal,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import * as Clipboard from "expo-clipboard";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";
import Colours from "../../utils/Colours";
import { SafeAreaView } from "react-native-safe-area-context";

// Responsive scaling utilities
const { width, height } = Dimensions.get("window");
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};
const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

export default function AcknowledgeRedemptionScreen({ visible }) {
  const { setAuth } = useAuth();
  const axiosPrivate = useAxiosPrivate();

  // Local state for redemption code
  const [redemptionCode, setRedemptionCode] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingCode, setFetchingCode] = useState(true);

  // Fetch redemption code automatically whenever modal opens
  useEffect(() => {
    let isMounted = true;

    if (!visible) return;

    const fetchRedemptionCode = async () => {
      setFetchingCode(true);
      try {
        const response = await axiosPrivate.get("/getRedemptionCode/getRedemptionCode");

        if (isMounted && response?.data?.redemptionCode) {
          setRedemptionCode(response.data.redemptionCode);
        }
      } catch (error) {
        console.error("Error fetching redemption code:", error);
      } finally {
        if (isMounted) {
          setFetchingCode(false);
        }
      }
    };

    fetchRedemptionCode();

    return () => {
      isMounted = false;
    };
  }, [visible]);

  // Copy code handler supporting Web, Expo, and React Native
  const handleCopyLink = async () => {
    if (!redemptionCode) return;

    try {
      if (Platform.OS === "web" && navigator.clipboard) {
        await navigator.clipboard.writeText(redemptionCode);
      } else if (Clipboard?.setStringAsync) {
        await Clipboard.setStringAsync(redemptionCode);
      }

      setIsCopied(true);

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy redemption code:", error);
    }
  };

  // Backend acknowledgment handler
  const handleAcknowledge = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.post("/acknowledge-redemption/acknowledge-redemption");

      // Verify successful status from backend response before updating context
      if (response?.data?.status === "success" || response?.status === 200) {
        // Update auth context to remove modal flag
        setAuth((prev) => ({
          ...prev,
          hasUnacknowledgedRedemption: false,
        }));

        // Clear local state
        setRedemptionCode("");
      } else {
        console.warn("Server responded without success status:", response?.data);
      }
    } catch (error) {
      console.error("Failed to acknowledge redemption code:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={false}
      statusBarTranslucent
      onRequestClose={() => {
        // Prevent hardware back button on Android from closing modal
      }}
    >
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />
        <View style={styles.container}>
          {/* Header Section */}
          <View style={styles.headerSection}>
            <View style={styles.iconCircle}>
              <Ionicons name="checkmark-circle" size={dynamicSize(48)} color={Colours.PRIMARY} />
            </View>
            <Text style={styles.title}>Order Reserved Successfully</Text>
            <Text style={styles.subtitle}>
              Please copy and save your redemption code. You will need it at the venue, after
              scanning the QR code presented to you by the waiter.
            </Text>
          </View>

          {/* Redemption Code Display Box */}
          <View style={styles.codeCard}>
            <View style={styles.codeDetails}>
              <Text style={styles.codeLabel}>REDEMPTION CODE</Text>
              {fetchingCode ? (
                <View style={styles.loaderContainer}>
                  <ActivityIndicator size="small" color={Colours.PRIMARY} />
                  <Text style={styles.loadingText}>Fetching code...</Text>
                </View>
              ) : (
                <Text style={styles.codeValue}>{redemptionCode || "NOT FOUND"}</Text>
              )}
            </View>

            {/* Copy Action Button */}
            <TouchableOpacity
              style={[
                styles.tagActionButton,
                isCopied && styles.tagActionButtonSuccess,
                (fetchingCode || !redemptionCode) && styles.disabledCopyButton,
              ]}
              activeOpacity={0.8}
              onPress={handleCopyLink}
              disabled={fetchingCode || !redemptionCode}
            >
              <Ionicons
                name={isCopied ? "checkmark-sharp" : "copy-outline"}
                size={dynamicSize(15)}
                color="#FFFFFF"
              />
              <Text style={styles.tagActionBtnText}>{isCopied ? "Copied!" : "Copy"}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.spacer} />

          {/* Footer Acknowledgment Button */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.acknowledgeButton, (loading || fetchingCode) && styles.disabledButton]}
              activeOpacity={0.8}
              onPress={handleAcknowledge}
              disabled={loading || fetchingCode}
            >
              {loading ? (
                <ActivityIndicator color="#FFFFFF" size="small" />
              ) : (
                <Text style={styles.acknowledgeButtonText}>I Have Saved My Code</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    paddingHorizontal: dynamicSpacing(20),
    paddingTop: dynamicSpacing(40),
    paddingBottom: dynamicSpacing(20),
  },
  headerSection: {
    alignItems: "center",
    marginBottom: dynamicSpacing(28),
  },
  iconCircle: {
    width: dynamicSize(80),
    height: dynamicSize(80),
    borderRadius: dynamicSize(40),
    backgroundColor: Colours.PRIMARY_LIGHTER,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: dynamicSpacing(16),
  },
  title: {
    fontSize: dynamicFontSize(22),
    fontWeight: "700",
    color: Colours.BLACK,
    textAlign: "center",
    marginBottom: dynamicSpacing(10),
  },
  subtitle: {
    fontSize: dynamicFontSize(14),
    color: Colours.DARKLIGHTGRAY,
    textAlign: "center",
    lineHeight: dynamicFontSize(20),
  },
  codeCard: {
    flexDirection: "row",
    backgroundColor: "white",
    padding: dynamicSpacing(16),
    borderRadius: dynamicSize(16),
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: dynamicSpacing(12),
    borderWidth: 1,
    borderColor: "#EBEBEF",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  codeDetails: {
    flex: 1,
    justifyContent: "center",
  },
  codeLabel: {
    fontSize: dynamicFontSize(11),
    fontWeight: "600",
    color: Colours.DARKLIGHTGRAY,
    letterSpacing: 0.5,
    marginBottom: dynamicSpacing(4),
  },
  codeValue: {
    fontSize: dynamicFontSize(20),
    fontWeight: "800",
    color: Colours.DARKGRAY,
    letterSpacing: 2,
  },
  loaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: dynamicSpacing(8),
  },
  loadingText: {
    fontSize: dynamicFontSize(13),
    color: Colours.DARKLIGHTGRAY,
  },
  tagActionButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colours.PRIMARY,
    paddingHorizontal: dynamicSpacing(14),
    paddingVertical: dynamicSpacing(10),
    borderRadius: dynamicSize(10),
    gap: dynamicSpacing(6),
  },
  tagActionButtonSuccess: {
    backgroundColor: Colours.DARKGREEN,
  },
  disabledCopyButton: {
    opacity: 0.5,
  },
  tagActionBtnText: {
    color: "#FFFFFF",
    fontSize: dynamicFontSize(13),
    fontWeight: "600",
  },
  spacer: {
    flex: 1,
  },
  footer: {
    width: "100%",
    paddingTop: dynamicSpacing(10),
  },
  acknowledgeButton: {
    backgroundColor: Colours.PRIMARY,
    height: dynamicSize(52),
    borderRadius: dynamicSize(14),
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  disabledButton: {
    opacity: 0.6,
  },
  acknowledgeButtonText: {
    color: "#FFFFFF",
    fontSize: dynamicFontSize(16),
    fontWeight: "700",
  },
});
